// Observe the selected variant, never write to the picker, form, price or cart.
// All frame-specific images must be real renderings of the same composition.
if (!customElements.get('pp-gallery-frame-preview')) {
  customElements.define('pp-gallery-frame-preview', class extends HTMLElement {
    connectedCallback() {
      this.life?.abort();
      this.life = new AbortController();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true, signal: this.life.signal });
      } else this.initialize();
    }

    initialize() {
      this.gallery = this.closest('media-gallery');
      this.scope = this.closest('.shopify-section');
      if (!this.gallery || !this.scope || this.dataset.productId !== '16064476873038') return;
      try { this.config = JSON.parse(this.querySelector('[data-pp-gallery-render-config]').textContent); }
      catch { return; }
      const frames = ['Schwarz', 'Weiß', 'Gold', 'Silber', 'Beige', 'Braun'];
      if (!Array.isArray(this.config.variants) || !this.config.variants.length ||
          frames.some(frame => !this.config.frames?.[frame]?.src || !this.config.frames[frame].srcset)) return;
      this.byId = new Map(this.config.variants.map(variant => [String(variant.id), variant]));
      const { signal } = this.life;
      // Gallery's existing picker deliberately avoids variant:update and section
      // replacement. It does synchronously update its native variant JSON.
      this.observer?.disconnect();
      this.observer = new MutationObserver(records => {
        if (records.some(record => {
          const element = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          return element?.closest('variant-picker') || [...record.addedNodes].some(node =>
            node.nodeType === 1 && (node.matches?.('variant-picker') || node.querySelector?.('variant-picker')));
        })) this.sync();
      });
      this.observer.observe(this.scope, { subtree: true, childList: true, characterData: true });
      this.scope.addEventListener('variant:selected', event => {
        if (event.target.closest?.('variant-picker')?.dataset.productId !== this.dataset.productId) return;
        this.nativePending = true;
        ++this.generation;
        this.frame = null;
        this.hideImages();
      }, { signal });
      this.scope.addEventListener('variant:update', event => {
        if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
        this.nativePending = false;
        this.sync();
      }, { signal });
      window.addEventListener('pageshow', () => this.sync(), { signal });
      this.generation = 0;
      this.frame = null;
      this.nativePending = false;
      this.sync();
    }

    images() { return [...this.gallery.querySelectorAll('img[data-pp-gallery-render]')]; }

    hideImages() {
      this.images().forEach(image => image.setAttribute('data-pp-render-pending', ''));
      this.gallery.setAttribute('aria-busy', 'true');
    }

    sync() {
      if (this.nativePending) return;
      const native = [...this.scope.querySelectorAll('variant-picker')]
        .find(picker => picker.dataset.productId === this.dataset.productId);
      let selected;
      try { selected = JSON.parse(native?.querySelector('script[type="application/json"]')?.textContent || 'null'); }
      catch { selected = null; }
      const variant = selected && this.byId.get(String(selected.id));
      const frame = variant?.frame === 'Natur' ? 'Beige' : variant?.frame;
      const rendering = this.config.frames[frame];
      if (!rendering) {
        ++this.generation;
        this.frame = null;
        this.hideImages();
        this.showError();
        return;
      }
      if (frame === this.frame) return;
      this.frame = frame;
      const generation = ++this.generation;
      this.imageLoads?.abort();
      this.imageLoads = new AbortController();
      this.clearError();
      // The server already renders the correct colour on first load. Keep it
      // visible; for a change, hide the old colour until the new file is ready.
      const images = this.images();
      const sources = new Set(rendering.srcset.split(',').map(source =>
        new URL(source.trim().split(/\s+/)[0], location.href).href));
      sources.add(new URL(rendering.src, location.href).href);
      const alreadyCorrect = images.every(image => image.dataset.ppFrame === frame &&
        image.src === new URL(rendering.src, location.href).href &&
        (!image.currentSrc || sources.has(new URL(image.currentSrc, location.href).href)));
      const status = this.querySelector('[data-pp-gallery-render-status]');
      if (alreadyCorrect) {
        images.forEach(image => image.removeAttribute('data-pp-render-pending'));
        if (status) status.textContent = '';
        return;
      }
      this.hideImages();
      if (status) status.textContent = 'Rahmenvorschau wird geladen.';
      const loader = new Image();
      this.loader = loader;
      loader.onload = () => {
        if (!this.isConnected || generation !== this.generation) return;
        const label = frame === 'Beige' ? 'Natur' : frame;
        this.images().forEach(image => {
          const reveal = () => {
            if (!this.isConnected || generation !== this.generation ||
                !sources.has(new URL(image.currentSrc || image.src, location.href).href)) return;
            image.removeAttribute('data-pp-render-pending');
            if (image.closest('slideshow-slide')) {
              this.gallery.removeAttribute('aria-busy');
              if (status) status.textContent = 'Rahmenvorschau: ' + label + '.';
            }
          };
          image.addEventListener('load', reveal, { signal: this.imageLoads.signal });
          image.addEventListener('error', () => {
            if (generation === this.generation) this.showError();
          }, { signal: this.imageLoads.signal });
          image.srcset = rendering.srcset;
          image.src = rendering.src;
          image.alt = 'Gallery Edition – Rahmen ' + label;
          image.dataset.ppFrame = frame;
          if (image.complete && image.naturalWidth > 0) reveal();
        });
      };
      loader.onerror = () => {
        if (this.isConnected && generation === this.generation) this.showError();
      };
      // Preload the same responsive candidate as the visible slide. Full-size
      // renderings are reserved for the existing lazy-loaded zoom image.
      loader.sizes = images.find(image => image.closest('slideshow-slide'))?.sizes || '100vw';
      loader.srcset = rendering.srcset;
      loader.src = rendering.src;
    }

    clearError() {
      this.gallery.querySelectorAll('.pp-gallery-render-error').forEach(error => error.remove());
      this.gallery.removeAttribute('aria-busy');
    }

    showError() {
      this.clearError();
      this.gallery.querySelectorAll('.product-media:has([data-pp-gallery-render])').forEach(media => {
        const message = document.createElement('span');
        message.className = 'pp-gallery-render-error';
        message.textContent = 'Die Rahmenvorschau konnte nicht geladen werden.';
        media.append(message);
      });
      const status = this.querySelector('[data-pp-gallery-render-status]');
      if (status) status.textContent = 'Die Rahmenvorschau konnte nicht geladen werden.';
    }

    disconnectedCallback() {
      ++this.generation;
      this.life?.abort();
      this.imageLoads?.abort();
      this.observer?.disconnect();
      if (this.loader) this.loader.onload = this.loader.onerror = null;
    }
  });
}
